import "./ConstructorPage.style.css";

import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ConstructorTable } from "./ConstructorTable/ConstructorTable";
import { ConstructorViewMode } from "../../utils/enums/ConstructorViewMode";
import { ConstructorGrid } from "./ConstructorGrid/ConstructorGrid";
import { GameCardState } from "../../utils/states/ConstructorPageStates/GameCardState";
import { ObjectState } from "../../utils/states/ConstructorPageStates/ObjectState";
import { ConstructorCard } from "./ConstructorCard/ConstructorCard";
import { PropertyState } from "../../utils/states/ConstructorPageStates/PropertyState";
import { Status } from "../../utils/enums/Status";
import { checkSearchValueForObjectName } from "../../utils/functions/checkSearchValueForObjectName";
import { ApiFactory } from "../../api-client/api-factory";
import {
  CollectionsApi,
  FieldDto,
  GamesApi,
  ObjectWithFieldsDto,
  SetFieldOrderRequest,
} from "../../api-client";
import { GameCardMapper } from "../../utils/mappers/GameCardMapper";
import { PropertiesMapper } from "../../utils/mappers/PropertiesMapper";
import { ObjectMapper } from "../../utils/mappers/ObjectMapper";
import { Link, useParams } from "react-router-dom";
import { UniqueId } from "../../utils/UniqueId";
import { DEFAULT_GAME_IMAGE } from "../../utils/constants/constants";
import { AppRouter } from "../../utils/enums/AppRouter";

import exitImage from "../../resources/images/exit.png";
import cardImage from "./../../resources/images/card_view.png";
import cardActiveImage from "./../../resources/images/card_active_view.png";
import tableImage from "./../../resources/images/table_view.png";
import tableActiveImage from "./../../resources/images/table_active_view.png";
import gridImage from "../../resources/images/grid_view.png";
import gridActiveImage from "../../resources/images/grid_active_view.png";
import saveImage from "./../../resources/images/save.png";
import savingImage from "./../../resources/images/saving.png";
import publishImage from "./../../resources/images/publish.png";
import hideImage from "./../../resources/images/hide.png";

export const ConstructorPage = () => {
  const { gameId } = useParams<string>();

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [isPublish, setIsPublish] = useState<boolean>(false);

  const [collectionId, setCollectionId] = useState<string>("new");
  const [selectedViewMode, setSelectedViewMode] = useState<ConstructorViewMode>(
    ConstructorViewMode.Table
  );

  const [searchValue, setSearchValue] = useState<string>("");

  const [gameCardState, setGameCardState] = useState<GameCardState>({
    apiId: null,
    image: DEFAULT_GAME_IMAGE,
    name: "",
    author: "",
    playersTodayCount: 0,
    playersAllCount: 0,
    favoriteCount: 0,
    tags: [],
  });

  const [objects, setObjects] = useState<ObjectState[]>([]);

  const [properties, setProperties] = useState<PropertyState[]>([]);
  const filteredProperties = properties.filter(
    (property) => property.status !== Status.Delete
  );

  const checkDataFilled = useCallback((): boolean => {
    const hasEmptyObject = objects.some((object) => {
      return (
        Object.values(object.properties).some((objectValue) => {
          return objectValue.value === "";
        }) ||
        object.name === "" ||
        object.image === ""
      );
    });

    if (hasEmptyObject) {
      return false;
    }

    const hasEmptyPropertyName = properties.some((property) => {
      return property.name === "";
    });

    if (hasEmptyPropertyName) {
      return false;
    }

    return gameCardState.name !== "" && gameCardState.image !== "";
  }, [properties, objects, gameCardState]);

  const [isDataFilled, setIsDataFilled] = useState<boolean>(checkDataFilled());
  const [isCreated, setIsCreated] = useState<boolean>(!gameId);

  const getFilteredObjects = (): ObjectState[] => {
    let filteredObjects: ObjectState[];

    filteredObjects = objects.filter(
      (object) => object.status !== Status.Delete
    );

    if (searchValue) {
      filteredObjects = filteredObjects.filter((object) =>
        checkSearchValueForObjectName(object.name, searchValue)
      );
    }

    return filteredObjects;
  };

  const filteredObjects = getFilteredObjects();

  useEffect(() => {
    setIsCreated(true);
  }, [objects, properties, gameCardState]);

  useEffect(() => {
    setIsDataFilled(checkDataFilled());
  }, [properties, objects, gameCardState, checkDataFilled]);

  useEffect(() => {
    UniqueId.clear();

    if (gameId) {
      ApiFactory.get(GamesApi)
        .gamesGameIdConstructorGet(gameId)
        .then((data) => {
          if (data.game) {
            setGameCardState(GameCardMapper.toGameCardState(data.game));
            setIsPublish(data.game.public!);
          }

          if (data.collection) {
            setCollectionId(data.collection.id!);
            const propertyStates = PropertiesMapper.toPropertyStates(
              data.collection.fields as FieldDto[]
            );
            setProperties(propertyStates);
            setObjects(
              ObjectMapper.toObjectStates(
                data.collection.objects as ObjectWithFieldsDto[],
                propertyStates
              )
            );
          }
        });
    }
  }, [gameId]);

  const handleSelectViewMode = (newViewMode: ConstructorViewMode) => {
    setSelectedViewMode(newViewMode);
  };

  const showConstructorView = () => {
    if (isSaving) {
      return <div />;
    }
    switch (selectedViewMode) {
      case ConstructorViewMode.Card:
        return (
          <ConstructorCard
            gameCardState={gameCardState}
            setGameCardState={setGameCardState}
          />
        );
      case ConstructorViewMode.Table:
        return (
          <ConstructorTable
            objects={filteredObjects}
            properties={filteredProperties}
            setProperties={setProperties}
            setObjects={setObjects}
          />
        );
      case ConstructorViewMode.Objects:
        return (
          <ConstructorGrid
            objects={filteredObjects}
            properties={filteredProperties}
            setObjects={setObjects}
          />
        );
    }
  };

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "search") {
      setSearchValue(value);
    }
  };

  const handleSaveGame = () => {
    setIsSaving(true);
    updateGame()
      .then(() => {
        setIsSaving(false);
      })
      .catch(() => {
        console.error("Не удалось сохранить")
        setIsSaving(false);
      });
  };

  const updateGame = async () => {
    const updatedProperties = properties.filter(
      (property) => property.status === Status.Update
    );
    const createdProperties = properties.filter(
      (property) => property.status === Status.Create
    );
    const deletedProperties = properties.filter(
      (property) => property.status === Status.Delete
    );

    const updatedObjects = objects.filter(
      (object) => object.status === Status.Update
    );
    const createdObjects = objects.filter(
      (object) => object.status === Status.Create
    );
    const deletedObjects = objects.filter(
      (object) => object.status === Status.Delete
    );

    try {
      await updateProperties(updatedProperties);

      const createdPropertySyncResults = await createProperties(
        createdProperties
      );
      const tempPropertyIdToSyncedPropertyMap = new Map<
        number,
        PropertyState
      >();
      createdPropertySyncResults.forEach((item) => {
        tempPropertyIdToSyncedPropertyMap.set(item.tempId, item.syncedProperty);
      });

      await deleteProperties(deletedProperties);

      let finalPropertiesState: PropertyState[] = [...properties];
      const deletedPropertyIds = new Set(deletedProperties.map((p) => p.id));
      finalPropertiesState = finalPropertiesState.filter(
        (p) => !deletedPropertyIds.has(p.id)
      );

      const updatedPropertyMap = new Map(
        updatedProperties.map((p) => [p.id, p])
      );
      finalPropertiesState = finalPropertiesState.map((p) =>
        updatedPropertyMap.has(p.id) ? updatedPropertyMap.get(p.id)! : p
      );

      finalPropertiesState = finalPropertiesState.map((prop) => {
        if (tempPropertyIdToSyncedPropertyMap.has(prop.id)) {
          return tempPropertyIdToSyncedPropertyMap.get(prop.id)!;
        }
        return prop;
      });

      const propertyIds = finalPropertiesState.map((p) => {
        if (!p.apiId) {
          throw new Error(
            `Property ${p.id} does not have an apiId after creation.`
          );
        }
        return p.apiId;
      });
      const setFieldOrderRequest =
        PropertiesMapper.toSetFieldOrderRequest(propertyIds);
      await updatePropertiesPosition(setFieldOrderRequest);

      await updateObjects(updatedObjects, propertyIds);

      const createdObjectSyncResults = await createObjects(
        createdObjects,
        propertyIds,
        finalPropertiesState
      );

      const tempObjectIdToSyncedObjectMap = new Map<number, ObjectState>();
      createdObjectSyncResults.forEach((item) => {
        tempObjectIdToSyncedObjectMap.set(item.tempId, item.syncedObject);
      });

      await deleteObjects(deletedObjects);

      let finalObjectsState: ObjectState[] = [...objects];

      const deletedObjectIds = new Set(deletedObjects.map((o) => o.id));
      finalObjectsState = finalObjectsState.filter(
        (o) => !deletedObjectIds.has(o.id)
      );

      const updatedObjectMap = new Map(updatedObjects.map((o) => [o.id, o]));
      finalObjectsState = finalObjectsState.map((o) =>
        updatedObjectMap.has(o.id) ? updatedObjectMap.get(o.id)! : o
      );

      finalObjectsState = finalObjectsState.map((obj) => {
        if (tempObjectIdToSyncedObjectMap.has(obj.id)) {
          return tempObjectIdToSyncedObjectMap.get(obj.id)!;
        }
        return obj;
      });

      await ApiFactory.get(GamesApi).gamesGameIdPut(
        gameId!,
        GameCardMapper.toUpdateGameRequest(gameCardState)
      );
      const newTags = await ApiFactory.get(GamesApi).gamesGameIdTagsPut(
        gameId!,
        gameCardState.tags
      );

      setGameCardState((prevState) => {
        return {
          ...prevState,
          tags: newTags!,
        };
      });

      setProperties(
        finalPropertiesState.map((finalPropertyState) => {
          return {
            ...finalPropertyState,
            status: Status.None,
          };
        })
      );

      setObjects(
        finalObjectsState.map((finalObjectState) => {
          return {
            ...finalObjectState,
            status: Status.None,
          };
        })
      );

      console.log("Данные сохранены");
    } catch (error) {
      console.error("Ошибка при сохранении данных");
    }
  };

  const updateProperties = (properties: PropertyState[]) => {
    const propertyPromises = properties.map((property) => {
      return ApiFactory.get(
        CollectionsApi
      ).collectionsCollectionIdFieldsFieldIdPut(
        collectionId as string,
        property.apiId as string,
        PropertiesMapper.toUpdateFieldRequest(property)
      );
    });

    return Promise.all(propertyPromises);
  };

  const createProperties = (createdProperties: PropertyState[]) => {
    const propertyPromises = createdProperties.map(async (property) => {
      const fieldDto = await ApiFactory.get(
        CollectionsApi
      ).collectionsCollectionIdFieldsPost(
        collectionId as string,
        PropertiesMapper.toCreateFieldRequest(property)
      );
      const syncedProperty = PropertiesMapper.toPropertyState(
        fieldDto,
        property.id
      );

      return { tempId: property.id, syncedProperty };
    });

    return Promise.all(propertyPromises);
  };

  const deleteProperties = (properties: PropertyState[]) => {
    const propertyPromises = properties.map(async (property) => {
      await ApiFactory.get(
        CollectionsApi
      ).collectionsCollectionIdFieldsFieldIdDelete(
        collectionId as string,
        property.apiId as string,
        PropertiesMapper.toUpdateFieldRequest(property)
      );
      UniqueId.deleteUniqueId(property.id);
    });

    return Promise.all(propertyPromises);
  };

  const updatePropertiesPosition = (
    setFieldOrderRequest: SetFieldOrderRequest
  ) => {
    return ApiFactory.get(
      CollectionsApi
    ).collectionsCollectionIdFieldsSetOrderPatch(
      collectionId as string,
      setFieldOrderRequest
    );
  };

  const updateObjects = (objects: ObjectState[], propertyIds: string[]) => {
    const objectPromises = objects.map(async (object) => {
      await ApiFactory.get(
        CollectionsApi
      ).collectionsCollectionIdObjectsObjectIdPut(
        collectionId as string,
        object.apiId as string,
        ObjectMapper.toUpdateObjectRequest(object, propertyIds)
      );
    });

    return Promise.all(objectPromises);
  };

  const createObjects = async (
    objectsToCreate: ObjectState[],
    propertyIds: string[],
    currentPropertiesState: PropertyState[]
  ): Promise<{ tempId: number; syncedObject: ObjectState }[]> => {
    const objectPromises = objectsToCreate.map(async (object) => {
      const objectDto = await ApiFactory.get(
        CollectionsApi
      ).collectionsCollectionIdObjectsPost(
        collectionId as string,
        ObjectMapper.toCreateObjectRequest(object, propertyIds)
      );
      const syncedObject = ObjectMapper.toObjectState(
        objectDto,
        currentPropertiesState,
        object.id
      );

      return { tempId: object.id, syncedObject };
    });

    return Promise.all(objectPromises);
  };

  const deleteObjects = (objects: ObjectState[]) => {
    const objectPromises = objects.map(async (object) => {
      await ApiFactory.get(
        CollectionsApi
      ).collectionsCollectionIdObjectsObjectIdDelete(
        collectionId as string,
        object.apiId as string
      );

      UniqueId.deleteUniqueId(object.id);
    });

    return Promise.all(objectPromises);
  };

  const handlePublishGame = (isPublic: boolean) => {
    if (gameId) {
      ApiFactory.get(GamesApi)
        .gamesGameIdSetPublicIsPublicPut(gameId, isPublic)
        .then(() => {
          setIsPublish((prevState) => !prevState);
          console.log(isPublic ? "игра опубликована" : "игра скрыта");
        })
        .catch(() => console.error("не получилось опубликовать/скрыть игру"));
    }
  };

  return (
    <div className="constructor_page_container">
      <div className="constructor_page__content">
        <div className="constructor_page__constructor_menu">
          <div className="constructor_page__constructor_view_buttons">
            <button
              className={
                selectedViewMode === ConstructorViewMode.Card
                  ? "constructor_page__constructor_view_button active"
                  : "constructor_page__constructor_view_button"
              }
              onClick={() => handleSelectViewMode(ConstructorViewMode.Card)}
            >
              <img
                src={
                  selectedViewMode === ConstructorViewMode.Card
                    ? cardActiveImage
                    : cardImage
                }
                alt={
                  selectedViewMode === ConstructorViewMode.Card
                    ? "active"
                    : "inactive"
                }
              />
            </button>
            <button
              className={
                selectedViewMode === ConstructorViewMode.Table
                  ? "constructor_page__constructor_view_button active"
                  : "constructor_page__constructor_view_button"
              }
              onClick={() => handleSelectViewMode(ConstructorViewMode.Table)}
            >
              <img
                src={
                  selectedViewMode === ConstructorViewMode.Table
                    ? tableActiveImage
                    : tableImage
                }
                alt={
                  selectedViewMode === ConstructorViewMode.Table
                    ? "active"
                    : "inactive"
                }
              />
            </button>
            <button
              className={
                selectedViewMode === ConstructorViewMode.Objects
                  ? "constructor_page__constructor_view_button active"
                  : "constructor_page__constructor_view_button"
              }
              onClick={() => handleSelectViewMode(ConstructorViewMode.Objects)}
            >
              <img
                src={
                  selectedViewMode === ConstructorViewMode.Objects
                    ? gridActiveImage
                    : gridImage
                }
                alt={
                  selectedViewMode === ConstructorViewMode.Objects
                    ? "active"
                    : "inactive"
                }
              />
            </button>
          </div>

          <div className="constructor_page__option_buttons">
            {isSaving ? (
              <button
                onClick={handleSaveGame}
                className={
                  !isDataFilled
                    ? "constructor_page__save_button_saving header_4 disabled"
                    : "constructor_page__save_button_saving header_4"
                }
                type="button"
                disabled={!isDataFilled}
              >
                <img src={savingImage} alt="save button" />
                Сохраняем...
              </button>
            ) : (
              <button
                onClick={handleSaveGame}
                className={
                  !isDataFilled
                    ? "constructor_page__save_button header_4 disabled"
                    : "constructor_page__save_button header_4"
                }
                type="button"
                disabled={!isDataFilled}
              >
                <img src={saveImage} alt="save button" />
                Сохранить
              </button>
            )}
            {!isPublish ? (
              <button
                onClick={() => handlePublishGame(true)}
                className={
                  !isCreated
                    ? "constructor_page__publish_button header_4 disabled"
                    : "constructor_page__publish_button header_4"
                }
                type="button"
                disabled={!isCreated}
              >
                <img src={publishImage} alt="publish button" />
                Опубликовать
              </button>
            ) : (
              <button
                onClick={() => handlePublishGame(false)}
                className={
                  !isCreated
                    ? "constructor_page__not_publish_button header_4 disabled"
                    : "constructor_page__not_publish_button header_4"
                }
                type="button"
                disabled={!isCreated}
              >
                <img src={hideImage} alt="publish button" />
                Скрыть из галереи
              </button>
            )}
            <Link to={AppRouter.MyGames} className="exit_button header_4">
              <img src={exitImage} alt="exit button" />
              Выйти
            </Link>
          </div>
        </div>
        <div className="constructor_page__constructor">
          {selectedViewMode !== ConstructorViewMode.Card &&
            objects.length !== 0 && (
              <input
                autoComplete="off"
                type="text"
                name="search"
                value={searchValue}
                className="constructor_page__search_object_input text_2"
                onChange={handleChangeSearch}
                placeholder="Поиск по названию..."
              />
            )}
          {showConstructorView()}
        </div>
      </div>
    </div>
  );
};
